import type { FieldConfig } from '../../../components/generic/fieldRenderers';

/** Detail/list label for related PMS records — name only (no codes). */
export function formatPmsRelationLabel(
  rel:
    | {
        code?: string | null;
        name?: string | null;
        title?: string | null;
        abrv?: string | null;
        equipmentCode?: string | null;
        jobCode?: string | null;
      }
    | null
    | undefined,
  fallbackId?: number | null,
): string {
  if (!rel && fallbackId == null) return '-';
  const name = rel?.name ?? rel?.title;
  if (name) return name;
  return (
    rel?.abrv ??
    rel?.code ??
    rel?.equipmentCode ??
    rel?.jobCode ??
    (fallbackId != null ? `#${fallbackId}` : '-')
  );
}

export function mapCodeNameSelectOption(item: {
  id: number;
  code?: string;
  name?: string;
  title?: string;
  abrv?: string;
  jobCode?: string;
  equipmentCode?: string;
  serialNumber?: string | null;
  vesselName?: string;
  vesselAbrv?: string | null;
  workOrderNumber?: string;
  intervalValue?: number | string | null;
}): { value: number; label: string } {
  if (item.vesselName) {
    return {
      value: item.id,
      label: item.vesselName,
    };
  }
  if (item.workOrderNumber) {
    return {
      value: item.id,
      label: item.title ? `${item.workOrderNumber} — ${item.title}` : item.workOrderNumber,
    };
  }
  if (item.serialNumber) {
    return {
      value: item.id,
      label: item.serialNumber,
    };
  }
  if (item.intervalValue != null && item.title == null && item.name == null && item.code == null) {
    const scheduleTypeName =
      (item as { scheduleType?: { name?: string; code?: string } }).scheduleType?.name ??
      (item as { scheduleType?: { name?: string; code?: string } }).scheduleType?.code;
    return {
      value: item.id,
      label: scheduleTypeName
        ? `${scheduleTypeName} (${item.intervalValue})`
        : `Schedule #${item.id} (${item.intervalValue})`,
    };
  }
  // Always prefer human name in PMS selects (codes stay internal).
  const name = item.name ?? item.title;
  if (name) return { value: item.id, label: name };
  return {
    value: item.id,
    label: item.abrv ?? item.code ?? item.equipmentCode ?? item.jobCode ?? String(item.id),
  };
}

type LookupSelectOpts = {
  name: string;
  label: string;
  apiEndpoint: string;
  required?: boolean;
  placeholder?: string;
  /** When true, allow clearing (None option). Default: !required */
  clearable?: boolean;
  optionsDependOnFields?: string[];
  optionsEndpointFromFormData?: FieldConfig['optionsEndpointFromFormData'];
  filterDynamicOptionItem?: FieldConfig['filterDynamicOptionItem'];
  filterApiOptionItem?: FieldConfig['filterApiOptionItem'];
};

export function pmsLookupSelect({
  name,
  label,
  apiEndpoint,
  required = true,
  placeholder,
  clearable,
  optionsDependOnFields,
  optionsEndpointFromFormData,
  filterDynamicOptionItem,
  filterApiOptionItem,
}: LookupSelectOpts): FieldConfig {
  const allowClear = clearable ?? !required;
  return {
    name,
    label,
    type: 'select',
    placeholder: placeholder ?? `Select ${label.toLowerCase()}`,
    apiEndpoint: optionsEndpointFromFormData ? undefined : apiEndpoint,
    optionsEndpointFromFormData,
    optionsDependOnFields,
    valueField: 'id',
    mapApiItemToSelectOption: mapCodeNameSelectOption,
    filterApiOptionItem:
      filterApiOptionItem ??
      ((item: unknown) => (item as { isActive?: boolean }).isActive !== false),
    filterDynamicOptionItem,
    required,
    validation: required ? { required: true } : undefined,
    options: allowClear ? [{ value: '', label: 'None' }] : [],
  };
}

export const pmsVesselField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'vesselId',
    label: 'Vessel',
    apiEndpoint: '/vessels?page=1&limit=500',
    required,
    placeholder: 'Select vessel',
  });

export const pmsRecordStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'recordStatusId',
    label: 'Record status',
    apiEndpoint: '/pms-record-statuses?page=1&limit=500',
    required,
  });

export const pmsCriticalityField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'criticalityId',
    label: 'Criticality',
    apiEndpoint: '/pms-criticalities?page=1&limit=500',
    required,
  });

export const pmsDepartmentTypeField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'typeId',
    label: 'Department type',
    apiEndpoint: '/pms-department-types?page=1&limit=500',
    required,
  });

export const pmsEquipmentStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'statusId',
    label: 'Equipment status',
    apiEndpoint: '/pms-equipment-statuses',
    required,
  });

export const pmsVerificationStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'verificationStatusId',
    label: 'Verification status',
    apiEndpoint: '/pms-verification-statuses',
    required,
  });

export const pmsDepartmentField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'departmentId',
    label: 'Department',
    apiEndpoint: '/pms-departments',
    required,
    optionsDependOnFields: ['vesselId'],
    optionsEndpointFromFormData: (fd) => {
      if (fd.vesselId) return `/pms-departments`;
      return `/pms-departments`;
    },
    filterDynamicOptionItem: (item, fd) => {
      if (!fd.vesselId) return true;
      return Number(item.vesselId) === Number(fd.vesselId);
    },
  });

export const pmsSystemField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'systemId',
    label: 'Equipment system',
    apiEndpoint: '/pms-equipment-systems',
    required,
    clearable: !required,
    optionsDependOnFields: ['vesselId', 'departmentId'],
    optionsEndpointFromFormData: () => '/pms-equipment-systems',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.departmentId && Number(item.departmentId) !== Number(fd.departmentId)) return false;
      if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
      return true;
    },
  });

export const pmsComponentField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'componentId',
    label: 'Component',
    apiEndpoint: '/pms-components',
    required,
    clearable: !required,
    optionsDependOnFields: ['systemId'],
    optionsEndpointFromFormData: () => '/pms-components',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.systemId && Number(item.systemId) !== Number(fd.systemId)) return false;
      return true;
    },
  });

export const pmsSubComponentField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'subComponentId',
    label: 'Sub-component',
    apiEndpoint: '/pms-sub-components',
    required,
    clearable: true,
    optionsDependOnFields: ['componentId'],
    optionsEndpointFromFormData: () => '/pms-sub-components',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.componentId && Number(item.componentId) !== Number(fd.componentId)) return false;
      return true;
    },
  });

export const pmsEquipmentField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'equipmentId',
    label: 'Equipment',
    apiEndpoint: '/pms-equipment',
    required,
    placeholder: 'Select equipment',
  });

export const pmsEquipmentInstanceStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'statusId',
    label: 'Instance status',
    apiEndpoint: '/pms-equipment-instance-statuses',
    required,
  });

/** Optional prior serial in the replacement chain (same equipment slot when selected). */
export const pmsPreviousInstanceField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'previousInstanceId',
    label: 'Previous instance',
    apiEndpoint: '/pms-equipment-instances',
    required,
    clearable: true,
    placeholder: 'None',
    optionsDependOnFields: ['equipmentId'],
    optionsEndpointFromFormData: () => '/pms-equipment-instances',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.id && Number(item.id) === Number(fd.id)) return false;
      if (fd.equipmentId && Number(item.equipmentId) !== Number(fd.equipmentId)) return false;
      return true;
    },
    filterApiOptionItem: () => true,
  });

export const pmsJobTypeField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'jobTypeId',
    label: 'Job type',
    apiEndpoint: '/pms-job-types',
    required,
  });

export const pmsMaintenanceStrategyField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'maintenanceStrategyId',
    label: 'Maintenance strategy',
    apiEndpoint: '/pms-maintenance-strategies',
    required,
  });

export const pmsCrewRankField = (
  name: 'responsibleRankId' | 'secondaryRankId' | 'assignedRankId',
  label: string,
  required = false,
): FieldConfig =>
  pmsLookupSelect({
    name,
    label,
    apiEndpoint: '/crew-lookups/ranks?page=1&limit=500',
    required,
    clearable: !required,
    placeholder: `Select ${label.toLowerCase()}`,
  });

export const pmsCounterTypeField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'counterTypeId',
    label: 'Counter type',
    apiEndpoint: '/pms-counter-types',
    required,
  });

export const pmsCounterSourceField = (
  name: 'sourceId' = 'sourceId',
  label = 'Counter source',
  required = true,
): FieldConfig =>
  pmsLookupSelect({
    name,
    label,
    apiEndpoint: '/pms-counter-sources',
    required,
  });

export const pmsIntervalUnitField = (
  name: string,
  label: string,
  required = false,
): FieldConfig =>
  pmsLookupSelect({
    name,
    label,
    apiEndpoint: '/pms-interval-units',
    required,
    clearable: !required,
  });

export const pmsMaintenanceCounterField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'counterId',
    label: 'Depends on counter',
    apiEndpoint: '/pms-maintenance-counters',
    required,
    clearable: !required,
    placeholder: 'Select counter',
  });

/** Equipment scoped to selected vessel when vesselId is set. */
export const pmsEquipmentForVesselField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'equipmentId',
    label: 'Equipment',
    apiEndpoint: '/pms-equipment',
    required,
    clearable: !required,
    placeholder: 'Select equipment',
    optionsDependOnFields: ['vesselId'],
    optionsEndpointFromFormData: () => '/pms-equipment',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
      return true;
    },
  });

export const pmsWorkOrderStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'statusId',
    label: 'Work order status',
    apiEndpoint: '/pms-work-order-statuses?page=1&limit=500',
    required,
  });

export const pmsWorkOrderSourceField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'sourceTypeId',
    label: 'Source',
    apiEndpoint: '/pms-work-order-sources?page=1&limit=500',
    required,
  });

export const pmsApprovalStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'approvalStatusId',
    label: 'Approval status',
    apiEndpoint: '/pms-approval-statuses?page=1&limit=500',
    required,
  });

export const pmsEquipmentInstanceField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'equipmentInstanceId',
    label: 'Equipment instance',
    apiEndpoint: '/pms-equipment-instances',
    required,
    clearable: true,
    optionsDependOnFields: ['equipmentId'],
    optionsEndpointFromFormData: () => '/pms-equipment-instances',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.equipmentId && Number(item.equipmentId) !== Number(fd.equipmentId)) return false;
      return true;
    },
    filterApiOptionItem: () => true,
  });

export const pmsMaintenanceJobField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'maintenanceJobId',
    label: 'Maintenance job',
    apiEndpoint: '/pms-maintenance-jobs',
    required,
    clearable: !required,
    optionsDependOnFields: ['equipmentId'],
    optionsEndpointFromFormData: () => '/pms-maintenance-jobs',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.equipmentId && Number(item.equipmentId) !== Number(fd.equipmentId)) return false;
      return true;
    },
  });

export const pmsMaintenanceScheduleField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'maintenanceScheduleId',
    label: 'Maintenance schedule',
    apiEndpoint: '/pms-maintenance-schedules',
    required,
    clearable: true,
    optionsDependOnFields: ['maintenanceJobId'],
    optionsEndpointFromFormData: () => '/pms-maintenance-schedules',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.maintenanceJobId && Number(item.maintenanceJobId) !== Number(fd.maintenanceJobId)) return false;
      return true;
    },
    filterApiOptionItem: () => true,
  });

export const pmsAssignedUserField = (required = false): FieldConfig => ({
  name: 'assignedUserId',
  label: 'Assigned user',
  type: 'select',
  placeholder: 'Select user',
  apiEndpoint: '/users?page=1&limit=500',
  valueField: 'id',
  mapApiItemToSelectOption: (item: {
    id: number;
    userDisplayName?: string | null;
    userEmail?: string;
    userFirstName?: string;
    userLastName?: string;
  }) => ({
    value: item.id,
    label:
      item.userDisplayName ||
      [item.userFirstName, item.userLastName].filter(Boolean).join(' ') ||
      item.userEmail ||
      String(item.id),
  }),
  filterApiOptionItem: (item: unknown) => (item as { userIsActive?: boolean }).userIsActive !== false,
  required,
  validation: required ? { required: true } : undefined,
  options: required ? [] : [{ value: '', label: 'None' }],
});

export const pmsDefectSeverityField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'severityId',
    label: 'Severity',
    apiEndpoint: '/pms-defect-severities',
    required,
  });

export const pmsDefectStatusField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'statusId',
    label: 'Defect status',
    apiEndpoint: '/pms-defect-statuses',
    required,
  });

export const pmsWorkOrderField = (required = false): FieldConfig =>
  pmsLookupSelect({
    name: 'workOrderId',
    label: 'Work order',
    apiEndpoint: '/pms-work-orders',
    required,
    clearable: true,
    optionsDependOnFields: ['vesselId', 'equipmentId'],
    optionsEndpointFromFormData: () => '/pms-work-orders',
    filterDynamicOptionItem: (item, fd) => {
      if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
      if (fd.equipmentId && Number(item.equipmentId) !== Number(fd.equipmentId)) return false;
      return true;
    },
    filterApiOptionItem: () => true,
  });

export const pmsDocumentTypeField = (required = true): FieldConfig =>
  pmsLookupSelect({
    name: 'documentTypeId',
    label: 'Document type',
    apiEndpoint: '/pms-document-types',
    required,
  });
